import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DateRangePicker as ReactDateRangePicker } from "react-date-range";
import { FiCalendar } from "react-icons/fi";
import {
  DATE_RANGE_PRESETS,
  detectDateRangePreset,
  formatDateRangeLabel,
  getDateRangeForPreset,
  parseLocalIsoDate,
  toLocalIsoDate,
} from "../../../utils";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./DateRangePicker.css";

const SELECTION_KEY = "selection";

function selectionFromIso(dateFrom, dateTo) {
  return {
    startDate: parseLocalIsoDate(dateFrom || toLocalIsoDate()),
    endDate: parseLocalIsoDate(dateTo || toLocalIsoDate()),
    key: SELECTION_KEY,
  };
}

/**
 * Reusable date range filter — preset list; calendar appears beside list for Custom range.
 * Popover is portaled to document.body so it is not clipped by overflow:hidden parents.
 */
export default function DateRangePicker({
  dateFrom,
  dateTo,
  preset: appliedPreset,
  onChange,
  label = "Date",
  className = "",
  maxDate = new Date(),
  presets = DATE_RANGE_PRESETS,
  placeholder = "Select dates",
  allowClear = false,
}) {
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [highlightedPreset, setHighlightedPreset] = useState(() => {
    if (appliedPreset) return appliedPreset;
    if (!dateFrom || !dateTo) return "";
    return detectDateRangePreset(dateFrom, dateTo);
  });
  const [draft, setDraft] = useState(() => selectionFromIso(dateFrom, dateTo));

  const presetOptions = presets.filter((p) => p.id !== "custom");
  const customPreset = presets.find((p) => p.id === "custom") || {
    id: "custom",
    label: "Custom range",
  };

  const resolveAppliedPreset = () => {
    if (appliedPreset) return appliedPreset;
    if (!dateFrom || !dateTo) return "";
    return detectDateRangePreset(dateFrom, dateTo);
  };

  const resetPanelState = () => {
    const active = resolveAppliedPreset();
    setHighlightedPreset(active);
    setDraft(selectionFromIso(dateFrom, dateTo));
    setShowCalendar(active === "custom");
  };

  useEffect(() => {
    if (!open) {
      resetPanelState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, appliedPreset, open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePos = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const popoverWidth = showCalendar
        ? Math.min(window.innerWidth - 24, 640)
        : 320;
      const edge = 12;
      const gap = 6;
      // Horizontal: keep the panel fully inside the viewport (shift left if it would overflow right,
      // and never go past the left edge).
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - edge) {
        left = window.innerWidth - edge - popoverWidth;
      }
      left = Math.max(edge, left);
      // Vertical: open below the field; if it does not fit there, flip above it; if it fits in
      // neither place, pin it inside the viewport (its own max-height + scroll keep it reachable).
      const popoverHeight = popoverRef.current?.offsetHeight || 0;
      const spaceBelow = window.innerHeight - rect.bottom - gap - edge;
      const spaceAbove = rect.top - gap - edge;
      let top = rect.bottom + gap;
      if (popoverHeight > spaceBelow) {
        if (popoverHeight <= spaceAbove) {
          top = rect.top - gap - popoverHeight;
        } else {
          top = Math.max(edge, window.innerHeight - edge - popoverHeight);
        }
      }
      setPopoverPos({ top, left });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, showCalendar]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openPanel = () => {
    resetPanelState();
    setOpen(true);
  };

  const handleRangeChange = (item) => {
    const next = item[SELECTION_KEY];
    if (!next?.startDate || !next?.endDate) return;
    setDraft({
      startDate: next.startDate,
      endDate: next.endDate,
      key: SELECTION_KEY,
    });
    setHighlightedPreset("custom");
    setShowCalendar(true);
  };

  const handlePresetClick = (presetId) => {
    if (presetId === "custom") {
      setHighlightedPreset("custom");
      setShowCalendar(true);
      return;
    }
    const range = getDateRangeForPreset(presetId);
    setDraft(selectionFromIso(range.dateFrom, range.dateTo));
    setHighlightedPreset(presetId);
    setShowCalendar(false);
  };

  const handleApply = () => {
    let orderedFrom;
    let orderedTo;
    let nextPreset = highlightedPreset || "custom";

    if (highlightedPreset && highlightedPreset !== "custom") {
      const range = getDateRangeForPreset(highlightedPreset);
      orderedFrom = range.dateFrom;
      orderedTo = range.dateTo;
      nextPreset = range.preset || highlightedPreset;
    } else {
      const from = toLocalIsoDate(draft.startDate);
      const to = toLocalIsoDate(draft.endDate);
      orderedFrom = from <= to ? from : to;
      orderedTo = from <= to ? to : from;
      nextPreset = "custom";
    }

    onChange({
      dateFrom: orderedFrom,
      dateTo: orderedTo,
      preset: nextPreset,
    });
    setOpen(false);
  };

  const handleCancel = () => {
    resetPanelState();
    setOpen(false);
  };

  const handleClear = () => {
    onChange({
      dateFrom: "",
      dateTo: "",
      preset: "",
    });
    setHighlightedPreset("");
    setShowCalendar(false);
    setDraft(selectionFromIso("", ""));
    setOpen(false);
  };

  const hasValue = Boolean(dateFrom && dateTo);
  const displayLabel = hasValue ? formatDateRangeLabel(dateFrom, dateTo) : placeholder;

  const popover = open
    ? createPortal(
        <div
          ref={popoverRef}
          className={`ns-daterange__popover ns-daterange__popover--portal${
            showCalendar ? " ns-daterange__popover--with-calendar" : ""
          }`}
          role="dialog"
          aria-label={label || "Date"}
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <div className="ns-daterange__body">
            <div className="ns-daterange__sidebar">
              <ul className="ns-daterange__preset-list" role="listbox">
                {presetOptions.map((p) => (
                  <li key={p.id} role="option" aria-selected={highlightedPreset === p.id}>
                    <button
                      type="button"
                      className={`ns-daterange__preset-item${
                        highlightedPreset === p.id ? " ns-daterange__preset-item--active" : ""
                      }`}
                      onClick={() => handlePresetClick(p.id)}
                    >
                      {p.label}
                    </button>
                  </li>
                ))}
                <li role="option" aria-selected={highlightedPreset === "custom"}>
                  <button
                    type="button"
                    className={`ns-daterange__preset-item${
                      highlightedPreset === "custom" ? " ns-daterange__preset-item--active" : ""
                    }`}
                    onClick={() => handlePresetClick("custom")}
                  >
                    {customPreset.label}
                  </button>
                </li>
              </ul>
            </div>

            {showCalendar && (
              <div className="ns-daterange__calendar-pane">
                <div className="ns-daterange__picker-wrap">
                  <ReactDateRangePicker
                    onChange={handleRangeChange}
                    showSelectionPreview
                    showDateDisplay={false}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    ranges={[draft]}
                    direction="horizontal"
                    staticRanges={[]}
                    inputRanges={[]}
                    maxDate={maxDate}
                    rangeColors={["#4c5fd5"]}
                    color="#4c5fd5"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="ns-daterange__footer">
            <span className="ns-daterange__footer-range">
              {formatDateRangeLabel(
                toLocalIsoDate(draft.startDate),
                toLocalIsoDate(draft.endDate)
              )}
            </span>
            <div className="ns-daterange__footer-actions">
              {allowClear ? (
                <button type="button" className="btn_theme_secondary" onClick={handleClear}>
                  Clear
                </button>
              ) : null}
              <button type="button" className="btn_theme_secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className="btn_theme_primary" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`ns-daterange ${className}`.trim()} ref={rootRef}>
      {label ? <label className="ns-daterange__label">{label}</label> : null}
      <div className="ns-daterange__trigger-wrap">
        <button
          type="button"
          className="ns-daterange__trigger form-control"
          onClick={() => (open ? setOpen(false) : openPanel())}
          aria-expanded={open}
          aria-haspopup="dialog"
          title={displayLabel}
        >
          <FiCalendar className="ns-daterange__icon" aria-hidden />
          <span className="ns-daterange__trigger-value">{displayLabel}</span>
        </button>
        {allowClear && hasValue ? (
          <button
            type="button"
            className="ns-daterange__clear"
            aria-label="Clear date"
            title="Clear date"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClear();
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {popover}
    </div>
  );
}
